// Micro-insight generation for the assessment flow
// Based on operational research and industry benchmarks
import { DIMENSIONS, QUESTIONS } from './questions';

// Research-informed insights for specific patterns
// NOTE: These insights reflect general industry patterns. Specific figures are illustrative.
const RESEARCH_INSIGHTS = {
  strategic_low: {
    type: 'critical',
    stat: null,
    text: "**Strategic Clarity** is showing gaps. Research consistently shows that strategy execution failures often trace back to unclear priorities at the operational level.",
    source: "Organizational alignment research"
  },
  process_low: {
    type: 'critical',
    stat: null,
    text: "Your **Process Efficiency** responses suggest significant manual overhead. Organizations often underestimate the true cost of process inefficiency.",
    source: "Operational excellence research"
  },
  data_low: {
    type: 'critical',
    stat: null,
    text: "**Data & Decision Flow** gaps detected. Organizations with unified data access consistently make faster, more confident decisions.",
    source: "Digital transformation studies"
  },
  tech_low: {
    type: 'warning',
    stat: null,
    text: "Your **Tech Stack** responses indicate integration challenges. Disconnected tools are a common source of hidden operational drag.",
    source: "Enterprise technology research"
  },
  people_low: {
    type: 'critical',
    stat: null,
    text: "**Team Capacity** is under pressure. Senior talent spending time on low-value tasks is one of the most expensive inefficiencies.",
    source: "Workforce productivity research"
  },
  ai_low: {
    type: 'opportunity',
    stat: null,
    text: "**AI Readiness** is a gap — but also an opportunity. Organizations that move early on AI adoption tend to see compounding advantages.",
    source: "AI adoption research"
  },
  gap_pattern: {
    type: 'gap',
    text: "Interesting pattern: your **{best}** is strong, but **{worst}** is creating drag. This kind of imbalance often leaves significant operational capacity on the table.",
    source: "Unpaved AI Analysis"
  },
  strength_pattern: {
    type: 'strength',
    text: "Your **{best}** puts you ahead of typical organizations we assess. The question is whether your other dimensions can keep pace.",
    source: "Unpaved AI Benchmarks"
  }
};

// Generate contextual micro-insight based on current answers
export function generateMicroInsight(answers, currentQuestionIndex) {
  // Calculate average score per dimension from answers so far
  const dimensionAverages = {};
  
  DIMENSIONS.forEach(dimension => {
    const dimensionQuestions = QUESTIONS.filter(q => q.dimensionId === dimension.id);
    let totalScore = 0;
    let answeredCount = 0;
    
    dimensionQuestions.forEach(question => {
      const answerIndex = answers[question.id - 1];
      if (answerIndex !== undefined && answerIndex !== null) {
        totalScore += question.options[answerIndex].score;
        answeredCount++;
      }
    });
    
    if (answeredCount > 0) {
      dimensionAverages[dimension.id] = {
        name: dimension.name,
        average: totalScore / answeredCount,
        count: answeredCount
      };
    }
  });
  
  const dimensionEntries = Object.entries(dimensionAverages);
  if (dimensionEntries.length < 2) {
    return getDefaultInsight(currentQuestionIndex);
  }
  
  // Find best and worst dimensions
  let best = null;
  let worst = null;
  
  dimensionEntries.forEach(([id, data]) => {
    if (!best || data.average > best.average) {
      best = { id, ...data };
    }
    if (!worst || data.average < worst.average) {
      worst = { id, ...data };
    }
  });

  // Check for specific low-scoring dimensions with research-backed insights
  if (worst.average <= 2.5) {
    const researchKey = `${worst.id}_low`;
    if (RESEARCH_INSIGHTS[researchKey]) {
      return {
        ...RESEARCH_INSIGHTS[researchKey],
        highlight: { worst: worst.name }
      };
    }
  }
  
  // Gap pattern insight
  if (best.average >= 4 && worst.average <= 2.5) {
    const insight = RESEARCH_INSIGHTS.gap_pattern;
    return {
      type: insight.type,
      text: insight.text.replace('{best}', best.name).replace('{worst}', worst.name),
      source: insight.source,
      highlight: { best: best.name, worst: worst.name }
    };
  }
  
  // Strength pattern insight
  if (best.average >= 4) {
    const insight = RESEARCH_INSIGHTS.strength_pattern;
    return {
      type: insight.type,
      text: insight.text.replace('{best}', best.name),
      source: insight.source,
      highlight: { best: best.name }
    };
  }
  
  return {
    type: 'mixed',
    text: "We're seeing a mixed operational picture. Your responses suggest **areas of strength alongside structural gaps** that compound over time.",
    source: "Based on your responses",
    highlight: null
  };
}

// Default insights for specific question indices with research-informed stats
// NOTE: These insights are based on general industry patterns and research trends.
// Specific numbers are indicative rather than precise citations.
function getDefaultInsight(questionIndex) {
  const defaultInsights = {
    4: {
      type: 'benchmark',
      stat: null,
      text: "**Did you know?** Process efficiency consistently ranks as one of the top operational challenges for COOs — and most organizations underestimate its true cost.",
      source: "Industry operational surveys"
    },
    8: {
      type: 'benchmark',
      stat: null,
      text: "Organizations with fragmented data typically spend significant time each week reconciling conflicting information across teams.",
      source: "Digital transformation research"
    },
    11: {
      type: 'benchmark',
      stat: null,
      text: "Most mid-market companies use dozens of different SaaS tools, but only a fraction are properly integrated — the rest create manual work.",
      source: "SaaS management studies"
    },
    14: {
      type: 'benchmark',
      stat: null,
      text: "Research suggests senior leaders often spend a substantial portion of their time on work that could be automated or delegated.",
      source: "Workplace productivity research"
    },
    18: {
      type: 'benchmark',
      stat: null,
      text: "Organizations with formal AI adoption strategies tend to see significantly faster implementation and better outcomes.",
      source: "AI adoption research"
    }
  };
  
  return defaultInsights[questionIndex] || {
    type: 'progress',
    text: "You're making great progress. A few more questions and we'll have your **complete operational diagnostic**.",
    source: null,
    highlight: null
  };
}

// Parse text with bold markers
export function parseInsightText(text) {
  // Convert **text** to <strong>text</strong>
  const parts = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'bold', content: match[1] });
    lastIndex = regex.lastIndex;
  }
  
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }
  
  return parts;
}
